import React from 'react';
import { useTranslation } from 'react-i18next';
import { useFixedTooltip } from '../../hooks/useFixedTooltip';
import './DataCollected.scss';
import metricsService from '@/core/services/MetricsService';
import centralLimitsService from '@/core/services/CentralLimitsService';

const DATA_COLLECTED_VISIBILITY_EVENT = 'mfe-menu-data-collected-visibility-change';
const MENU_HAS_DATA_COLLECTED_ATTR = 'data-mfe-menu-has-data-collected';

export interface DataCollectedProps {
  labels?: {
    title?: string;
    limit?: string;
    listening?: string;
    proprietary?: string;
    twitterEnterprise?: string;
    of?: string;
    used?: string;
    unlimited?: string;
    monthlySuffix?: string;
    tooltipListening?: string;
    tooltipProprietary?: string;
    tooltipTwitterEnterprise?: string;
    tooltipCollectedData?: string;
    interactionsCount?: string;
  };
}

type UsageVariant = 'normal' | 'warning' | 'danger';

function getUsageVariant(percentage: number): UsageVariant {
  if (percentage >= 90) return 'danger';
  if (percentage >= 70) return 'warning';
  return 'normal';
}

export const DataCollected: React.FC<DataCollectedProps> = ({
  labels: customLabels,
}) => {
  const { t } = useTranslation();
  const [metricsState, setMetricsState] = React.useState({
    listeningValue: 0,
    proprietaryValue: 0,
    interactionsCountValue: 0,
    limit: 0,
    isUnlimited: true,
    limitPeriod: 'total' as 'total' | 'monthly',
    twitter: {
      collected: 0,
      limit: 0,
    },
  });

  React.useEffect(() => {
    document.documentElement.setAttribute(MENU_HAS_DATA_COLLECTED_ATTR, 'true');
    document.dispatchEvent(
      new CustomEvent(DATA_COLLECTED_VISIBILITY_EVENT, {
        detail: { visible: true },
      }),
    );

    return () => {
      document.documentElement.removeAttribute(MENU_HAS_DATA_COLLECTED_ATTR);
      document.dispatchEvent(
        new CustomEvent(DATA_COLLECTED_VISIBILITY_EVENT, {
          detail: { visible: false },
        }),
      );
    };
  }, []);

  React.useEffect(() => {
    let isMounted = true;

    const loadMetrics = async () => {
      try {
        const response = await metricsService.getUniverseLimits();
        const limits = response?.limits;
        const twitter = response?.twitter_enterprise;

        if (!limits || !isMounted) {
          return;
        }

        const monthLimit = limits.month_posts_limit ?? 0;
        const totalLimit = limits.total_posts_limit ?? 0;
        const shouldUseMonthlyMetrics = monthLimit > 0;
        const resolvedListeningValue = shouldUseMonthlyMetrics
          ? (limits.total_month_listening_posts ?? 0)
          : (limits.total_listening_posts ?? 0);
        const resolvedProprietaryValue = shouldUseMonthlyMetrics
          ? (limits.total_month_proprietary_posts ?? 0)
          : (limits.total_proprietary_posts ?? 0);
        const resolvedLimit = shouldUseMonthlyMetrics ? monthLimit : totalLimit;
        const resolvedInteractionsCountValue = resolvedListeningValue + resolvedProprietaryValue;

        setMetricsState({
          listeningValue: resolvedListeningValue,
          proprietaryValue: resolvedProprietaryValue,
          interactionsCountValue: resolvedInteractionsCountValue,
          limit: resolvedLimit,
          isUnlimited: resolvedLimit <= 0,
          limitPeriod: shouldUseMonthlyMetrics ? 'monthly' : 'total',
          twitter: {
            collected: twitter?.collected ?? 0,
            limit: twitter?.limit ?? 0,
          },
        });
      } catch {
        if (!isMounted) {
          return;
        }

        setMetricsState((currentState) => currentState);
      }
    };

    void loadMetrics();

    return () => {
      isMounted = false;
    };
  }, []);

  const [hasUsergroupLimits, setHasUsergroupLimits] = React.useState(false);

  React.useEffect(() => {
    let isMounted = true;

    const loadCentralLimits = async () => {
      try {
        const response = await centralLimitsService.getLimitByAccountId();

        if (isMounted) {
          setHasUsergroupLimits(!!response?.usergroup_limits);
        }
      } catch {
        // leave hasUsergroupLimits as false on error
      }
    };

    void loadCentralLimits();

    return () => {
      isMounted = false;
    };
  }, []);

  const labels = React.useMemo(() => {
    const menuLabels = (t('menu', { returnObjects: true }) || {}) as Record<string, string>;
    return {
      title: customLabels?.title ?? menuLabels.dataCollectedTitle ?? '',
      limit: customLabels?.limit ?? menuLabels.dataCollectedLimit ?? '',
      listening: customLabels?.listening ?? menuLabels.dataCollectedListening ?? '',
      proprietary: customLabels?.proprietary ?? menuLabels.dataCollectedProprietary ?? '',
      twitterEnterprise: customLabels?.twitterEnterprise ?? menuLabels.dataCollectedTwitterEnterprise ?? '',
      of: customLabels?.of ?? menuLabels.dataCollectedOf ?? '',
      used: customLabels?.used ?? menuLabels.dataCollectedUsed ?? '',
      unlimited: customLabels?.unlimited ?? menuLabels.dataCollectedUnlimited ?? '',
      monthlySuffix: customLabels?.monthlySuffix ?? menuLabels.dataCollectedMonthlySuffix ?? '',
      tooltipListening: customLabels?.tooltipListening ?? menuLabels.dataCollectedTooltipListening ?? '',
      tooltipProprietary: customLabels?.tooltipProprietary ?? menuLabels.dataCollectedTooltipProprietary ?? '',
      tooltipTwitterEnterprise: customLabels?.tooltipTwitterEnterprise ?? menuLabels.dataCollectedTooltipTwitterEnterprise ?? '',
      tooltipCollectedData: customLabels?.tooltipCollectedData ?? menuLabels.dataCollectedTooltipCollectedData ?? '',
      interactionsCount: customLabels?.interactionsCount ?? menuLabels.dataCollectedInteractionsCount ?? '',
    };
  }, [customLabels, t]);

  const stats = React.useMemo(() => {
    const safeLimit = metricsState.limit || 1;
    const safeTwitterLimit = metricsState.twitter.limit || 1;
    return {
      total: metricsState.listeningValue,
      percentage: Math.min((metricsState.listeningValue / safeLimit) * 100, 100),
      listeningWidth: Math.min((metricsState.listeningValue / safeLimit) * 100, 100),
      twitter: {
        total: metricsState.twitter.collected,
        percentage: Math.min((metricsState.twitter.collected / safeTwitterLimit) * 100, 100),
        width: Math.min((metricsState.twitter.collected / safeTwitterLimit) * 100, 100),
      },
    };
  }, [metricsState.limit, metricsState.listeningValue, metricsState.twitter.limit, metricsState.twitter.collected]);

  const listeningUsageVariant = React.useMemo(
    () => getUsageVariant(stats.listeningWidth),
    [stats.listeningWidth],
  );

  const twitterUsageVariant = React.useMemo(
    () => getUsageVariant(stats.twitter.width),
    [stats.twitter.width],
  );

  const limitLabel = React.useMemo(() => {
    if (metricsState.isUnlimited) return labels.unlimited;
    const limitSuffix = metricsState.limitPeriod === 'monthly' ? (labels.monthlySuffix ?? '/mês') : '';
    return `${metricsState.limit.toLocaleString()}${limitSuffix}`;
  }, [metricsState.isUnlimited, metricsState.limit, metricsState.limitPeriod, labels.unlimited, labels.monthlySuffix]);

  const twitterLimitLabel = React.useMemo(() => {
    return `${metricsState.twitter.limit.toLocaleString()}`;
  }, [metricsState.twitter.limit]);

  const usageSummaryLabel = React.useMemo(() => {
    return `${metricsState.listeningValue.toLocaleString()} ${labels.of} ${limitLabel}`.trim();
  }, [limitLabel, metricsState.listeningValue, labels.of]);

  const twitterUsageSummaryLabel = React.useMemo(() => {
    return `${metricsState.twitter.collected.toLocaleString()} ${labels.of} ${twitterLimitLabel}`.trim();
  }, [twitterLimitLabel, metricsState.twitter.collected, labels.of]);

  const handleTooltipPosition = useFixedTooltip('.data-collected__custom-tooltip', 'top');

  return (
    <div className="data-collected">
      <div className="data-collected__header">
        <button 
          type="button"
          className="data-collected__info-wrapper" 
          onMouseEnter={handleTooltipPosition}
          aria-label={labels.title}
        >
          <span className="data-collected__title">
            {labels.title}
          </span>
          <span className="data-collected__custom-tooltip" role="tooltip">
            {labels.tooltipCollectedData}
          </span>
        </button>
      </div>
      {hasUsergroupLimits ? (
        <div className="data-collected__body">
          <span className="data-collected__label">{labels.interactionsCount}</span>
          <span className="data-collected__percentage">{metricsState.interactionsCountValue.toLocaleString()}</span>
        </div>
      ) : (
        <>
          <div className="data-collected__body">
            <div className="data-collected__item-info">
              <button 
                type="button"
                className="data-collected__info-wrapper" 
                onMouseEnter={handleTooltipPosition}
                aria-label={labels.listening}
              >
                <span className="data-collected__label">{labels.listening}</span>
                <span className="data-collected__custom-tooltip" role="tooltip">
                  {labels.tooltipListening}
                </span>
              </button>
            </div>
            <div className="data-collected__percentage">
              {!metricsState.isUnlimited && <span>{stats.percentage.toFixed(0)}%</span>}
            </div>
          </div>
          <div className="data-collected__progress-container">
            {!metricsState.isUnlimited ? (
              <div className="data-collected__progress-bar">
                <div 
                  className={
                    'data-collected__progress-fill data-collected__progress-fill--listening ' +
                    'data-collected__progress-fill--' + listeningUsageVariant
                  }
                  style={{ width: `${stats.listeningWidth}%` }}
                />
              </div>
            ) : (
              <div className="data-collected__progress-bar">
                <div 
                  className="data-collected__progress-fill data-collected__progress-fill--unlimited" 
                  style={{ width: '100%' }}
                />
              </div>
            )}
          </div>

          <div className="data-collected__item data-collected__item--summary">
            <div className="data-collected__item-info"></div>
            <span className="data-collected__summary-text">
              {usageSummaryLabel}
            </span>
          </div>
        </>
      )}

      {metricsState.twitter.limit > 0 && (
        <>
          <div className="data-collected__body" style={{ marginTop: '16px' }}>
            <div className="data-collected__item-info">
              <button 
                type="button"
                className="data-collected__info-wrapper" 
                onMouseEnter={handleTooltipPosition}
                aria-label={labels.twitterEnterprise}
              >
                <span className="data-collected__label">{labels.twitterEnterprise}</span>
                <span className="data-collected__custom-tooltip" role="tooltip">
                  {labels.tooltipTwitterEnterprise}
                </span>
              </button>
            </div>
            <span className="data-collected__percentage">
              <span>{stats.twitter.percentage.toFixed(0)}%</span>
            </span>
          </div>
          <div className="data-collected__progress-container">
            <div className="data-collected__progress-bar">
              <div 
                className={
                  'data-collected__progress-fill data-collected__progress-fill--twitter ' +
                  'data-collected__progress-fill--' + twitterUsageVariant
                }
                style={{ width: `${stats.twitter.width}%` }}
              />
            </div>
          </div>

          <div className="data-collected__item data-collected__item--summary">
            <div className="data-collected__item-info"></div>
            <span className="data-collected__summary-text">
              {twitterUsageSummaryLabel}
            </span>
          </div>
        </>
      )}

      {!hasUsergroupLimits && (
        <div className="data-collected__body" style={{ marginTop: '16px' }}>
          <div className="data-collected__item-info">
            <button 
              type="button"
              className="data-collected__info-wrapper" 
              onMouseEnter={handleTooltipPosition}
              aria-label={labels.proprietary}
            >
              <span className="data-collected__label">{labels.proprietary}</span>
              <span className="data-collected__custom-tooltip" role="tooltip">
                {labels.tooltipProprietary}
              </span>
            </button>
          </div>
          <div className="data-collected__item data-collected__item--summary" style={{ width: 'auto' }}>
            <span className="data-collected__summary-text">{metricsState.proprietaryValue.toLocaleString()}</span>
          </div>
        </div>
      )}
    </div>
  );
};
